import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { formsApi } from '@/api/formsApi'
import { markProcedureJourneysStale } from '@/lib/queryClient'
import { receivedTestCodes } from '@/lib/lis/testCodeLabel'
import { useOptionalTenant, useTenant } from '@/providers/TenantProvider'

/**
 * The unified audit trail for one submission.
 *
 * One query for what used to be two: the backend merges the form trail and the
 * product trail and serves them as one feed (see `src/types/audit.ts`).
 *
 * Keyed on the tenant like every other query here, so switching tenant cannot
 * leave another tenant's trail painted while a refetch is in flight.
 */
export function useSubmissionAuditTrail(submissionId: string | null, enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery(auditTrailQuery(tenantId, submissionId, enabled))
}

function auditTrailQuery(tenantId: string | null | undefined, submissionId: string | null, enabled: boolean) {
  return {
    queryKey: ['audit-trail', tenantId, submissionId],
    enabled: enabled && !!tenantId && !!submissionId,
    queryFn: () => formsApi.getAuditTrail(submissionId as string),
    // Overrides the 5-minute app default. Field edits are saved through
    // `formsApi.saveFieldValue` rather than a TanStack mutation, so nothing
    // invalidates this key when one lands; without this, opening the panel
    // after an edit would show a cached trail missing the change that was
    // just made. A trail that is quietly behind is worse than a slow one.
    staleTime: 0,
  }
}

/**
 * Field id / cell key → the LIS test code its current value was received with
 * (z8nrz7g2wm), read off the same trail. Null skips the fetch (builder preview).
 */
export function useReceivedTestCodes(submissionId: string | null): ReadonlyMap<string, string> {
  // Optional: the renderer is also drawn outside the app shell (its SSR tests),
  // where there is no tenant and so nothing to fetch.
  const tenantId = useOptionalTenant()?.selectedTenantId
  const { data } = useQuery(auditTrailQuery(tenantId, submissionId, true))
  return useMemo(() => receivedTestCodes(data ?? []), [data])
}

/**
 * Mark the audit trail stale after a write that did not go through a mutation.
 *
 * Almost nothing needs this: the cache-wide rule in `QueryProvider` already
 * refreshes the trail after every successful `useMutation`. Field saves are the
 * exception — `formsApi.saveFieldValue` is called directly from the renderer,
 * so its rows would otherwise not appear in an open panel until it was closed
 * and opened again.
 */
export function useAuditTrailInvalidator() {
  const queryClient = useQueryClient()
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['audit-trail'] })
    // The same writes move a procedure's progress. Marked stale only, so the
    // procedures list refetches it on its next visit (z8nrz7e1c2).
    markProcedureJourneysStale(queryClient)
  }, [queryClient])
}
